import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { ArrowLeft, FileText } from 'lucide-react';

export default function Terms() {
  const { lang } = useT();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5DCC9] z-10 pt-safe">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <button
            type="button"
            onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/'); }}
            className="inline-flex items-center gap-1 text-sm font-bold text-gray-600"
            data-testid="terms-back-btn"
          >
            <ArrowLeft size={16} /> {lang === 'fr' ? 'Retour' : 'Back'}
          </button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={20} className="text-[#7A1F2B]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
            {lang === 'fr' ? 'Document légal' : 'Legal document'}
          </div>
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">
          {lang === 'fr' ? "Conditions d'utilisation" : 'Terms of Service'}
        </h1>
        <p className="text-xs text-gray-500 mb-8">
          {lang === 'fr' ? 'Dernière mise à jour : 26 juin 2026' : 'Last updated: June 26, 2026'}
        </p>

        {lang === 'fr' ? <FrenchTerms /> : <EnglishTerms />}
      </main>
    </div>
  );
}

function FrenchTerms() {
  return (
    <div className="space-y-6 text-gray-700">
      <Section title="1. Acceptation des conditions">
        En accédant à SoumissionPro (le « Service »), vous acceptez d'être lié par les présentes conditions
        d'utilisation. Si vous n'êtes pas d'accord avec l'une de ces conditions, n'utilisez pas le Service.
        Le Service est exploité au Québec (Canada) et est soumis aux lois québécoises et fédérales applicables.
      </Section>

      <Section title="2. Description du service">
        SoumissionPro est une application web mobile-first permettant aux entrepreneurs en construction du
        Canada de créer, gérer et envoyer des soumissions professionnelles, incluant la génération de PDF,
        la signature électronique à distance, le calcul automatique des taxes, et le suivi de chantier.
      </Section>

      <Section title="3. Compte utilisateur">
        Vous êtes responsable de la confidentialité de votre mot de passe et de toutes les activités qui
        surviennent sous votre compte. Vous devez nous informer immédiatement de toute utilisation non
        autorisée de votre compte. SoumissionPro ne saurait être tenu responsable de toute perte ou dommage
        découlant du non-respect de cette obligation.
      </Section>

      <Section title="4. Utilisation acceptable">
        Vous vous engagez à ne pas utiliser le Service pour : (a) violer toute loi applicable; (b) transmettre
        du contenu illicite, diffamatoire, frauduleux ou trompeur; (c) tenter d'accéder à des données d'autres
        utilisateurs sans autorisation; (d) introduire des virus ou nuire à l'infrastructure technique du Service.
      </Section>

      <Section title="5. Abonnement et paiement">
        L'accès au Service est offert selon un modèle d'abonnement mensuel ou annuel. Les frais sont facturés
        à l'avance et ne sont pas remboursables, sauf disposition contraire prévue par la loi québécoise. Vous
        pouvez annuler votre abonnement à tout moment depuis votre profil; l'annulation prend effet à la fin
        de la période de facturation en cours.
        <br /><br />
        <b>Paiement sur iOS (App Store) :</b> Les abonnements achetés via l'application iOS sont traités
        exclusivement par Apple In-App Purchase. Le paiement est débité de votre compte iTunes/Apple lors de
        la confirmation d'achat. Le renouvellement automatique peut être désactivé dans vos réglages
        iCloud/Apple ID au moins 24 heures avant la fin de la période en cours. Le <b>Contrat de Licence
        Utilisateur Final standard d'Apple (EULA)</b> s'applique en complément des présentes conditions pour
        tous les utilisateurs iOS (voir : apple.com/legal/internet-services/itunes/dev/stdeula).
        <br /><br />
        <b>Paiement sur le web :</b> Les abonnements achetés via soumissionpro.net sont traités par Stripe.
      </Section>

      <Section title="6. Soumissions et signatures électroniques">
        Les soumissions et signatures électroniques générées via le Service sont juridiquement reconnues au
        Canada en vertu de la Loi sur la protection des renseignements personnels et les documents électroniques
        (LPRPDE) et de la Loi concernant le cadre juridique des technologies de l'information (Québec). Vous êtes
        seul responsable de l'exactitude des informations saisies dans vos soumissions.
      </Section>

      <Section title="7. Propriété intellectuelle">
        Le Service, son apparence, son code, ses textes, et ses fonctionnalités sont la propriété exclusive de
        SoumissionPro. Le contenu que vous saisissez (soumissions, clients, matériaux, logos) demeure votre
        propriété. Vous nous accordez une licence limitée pour stocker, traiter et afficher ce contenu dans le
        cadre du Service.
      </Section>

      <Section title="8. Limitation de responsabilité">
        Le Service est fourni « tel quel » sans garantie d'aucune sorte. Dans les limites permises par la loi,
        SoumissionPro ne pourra être tenu responsable de dommages indirects, spéciaux ou consécutifs résultant
        de l'utilisation ou de l'incapacité d'utiliser le Service, y compris la perte de profits, de données ou
        d'opportunités d'affaires.
      </Section>

      <Section title="9. Résiliation">
        Nous nous réservons le droit de suspendre ou résilier votre compte en cas de violation des présentes
        conditions. Vos données resteront accessibles en téléchargement pendant 30 jours suivant la résiliation.
      </Section>

      <Section title="10. Modifications">
        Nous pouvons modifier ces conditions à tout moment. Les modifications importantes vous seront notifiées
        par courriel ou via l'application. La poursuite de l'utilisation du Service après notification constitue
        votre acceptation des nouvelles conditions.
      </Section>

      <Section title="11. Droit applicable">
        Les présentes conditions sont régies par les lois en vigueur au Québec (Canada). Tout litige sera soumis
        à la compétence exclusive des tribunaux du district judiciaire de Montréal.
      </Section>

      <Section title="12. Contact">
        Pour toute question concernant ces conditions, contactez-nous à : <b>support@soumissionpro.net</b>
      </Section>
    </div>
  );
}

function EnglishTerms() {
  return (
    <div className="space-y-6 text-gray-700">
      <Section title="1. Acceptance of Terms">
        By accessing SoumissionPro (the "Service"), you agree to be bound by these terms of service.
        The Service is operated in Quebec, Canada and subject to applicable Quebec and federal laws.
      </Section>
      <Section title="2. Service Description">
        SoumissionPro is a mobile-first web application allowing Canadian construction contractors to create,
        manage, and send professional quotes including PDF generation, remote e-signature, automatic tax
        calculation, and project tracking.
      </Section>
      <Section title="3. User Account">
        You are responsible for the confidentiality of your password and all activity under your account.
      </Section>
      <Section title="4. Acceptable Use">
        You agree not to use the Service to violate any law, transmit illegal content, or attempt to access
        other users' data without authorization.
      </Section>
      <Section title="5. Subscription and Payment">
        The Service is offered on a monthly or annual subscription basis. Fees are billed in advance and are
        non-refundable. You may cancel anytime from your profile.
        <br /><br />
        <b>iOS payment (App Store):</b> Subscriptions purchased via the iOS app are processed exclusively
        through Apple In-App Purchase. Payment is charged to your iTunes/Apple account upon purchase
        confirmation. Auto-renewal can be disabled in your iCloud/Apple ID settings at least 24 hours before
        the end of the current period. <b>Apple's Standard End User License Agreement (EULA)</b> applies in
        addition to these Terms for all iOS users (see: apple.com/legal/internet-services/itunes/dev/stdeula).
        <br /><br />
        <b>Web payment:</b> Subscriptions purchased via soumissionpro.net are processed by Stripe.
      </Section>
      <Section title="6. Electronic Signatures">
        Quotes and signatures generated via the Service are legally recognized in Canada under PIPEDA and the
        Quebec Act respecting the legal framework for information technology.
      </Section>
      <Section title="7. Intellectual Property">
        The Service and its features are the exclusive property of SoumissionPro. Content you input remains yours.
      </Section>
      <Section title="8. Limitation of Liability">
        The Service is provided "as is" without warranty. SoumissionPro is not liable for indirect or
        consequential damages.
      </Section>
      <Section title="9. Termination">
        We may suspend or terminate your account for violations. Your data is downloadable for 30 days after
        termination.
      </Section>
      <Section title="10. Changes">
        We may update these terms. Continued use after notification constitutes acceptance.
      </Section>
      <Section title="11. Governing Law">
        Governed by the laws of Quebec, Canada. Disputes resolved in the courts of Montreal.
      </Section>
      <Section title="12. Contact">
        Questions: <b>support@soumissionpro.net</b>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-black text-lg mb-2 text-[#7A1F2B]">{title}</h2>
      <p className="text-sm leading-relaxed">{children}</p>
    </section>
  );
}
